"use client";

import { useRouter } from "next/navigation";
import { useTheme } from "next-themes";
import { api } from "@/lib/api";
import { useClientTranslations } from "@/hooks/useClientTranslations";
import { useSignedInSession } from "@/hooks/useSignedInSession";
import { reloadIntoSwitchedOrg } from "@/utils/verifiedSession.utils";
import { Glyph } from "@/components/desktop/ui";
import type { MenuItem } from "@/components/desktop/ui";
import { DESKTOP_PATH } from "./useWindowManager";
export function useAccountMenu(): {
  orgGroup: MenuItem[];
  themeGroup: MenuItem[];
  logoutGroup: MenuItem[];
} {
  const {
    orgId,
    userId
  } = useSignedInSession();
  const router = useRouter();
  const tContextMenu = useClientTranslations("desktop.contextMenu");
  const tNav = useClientTranslations("navigation");
  const {
    theme,
    setTheme
  } = useTheme();
  const {
    data: userOrgs
  } = api.org.getMyOrgs.useQuery(undefined, {
    staleTime: 5 * 60_000
  });
  const switchOrgs = api.org.switchOrgs.useMutation({
    onSuccess: ({
      orgId: newOrgId
    }) => reloadIntoSwitchedOrg({
      session: {
        userId,
        orgId: newOrgId
      },
      landingPath: DESKTOP_PATH
    })
  });
  const hasMultipleOrgs = !!userOrgs && userOrgs.length > 1;
  const orgGroup: MenuItem[] = hasMultipleOrgs && userOrgs ? [{
    label: tContextMenu("switchOrganization"),
    submenu: userOrgs.map(org => {
      const isCurrentOrg = org.id === orgId;
      return {
        label: org.name,
        onSelect: () => {
          if (!isCurrentOrg) {
            switchOrgs.mutate({
              newOrgId: org.id
            });
          }
        },
        disabled: isCurrentOrg || switchOrgs.isPending,
        // boolean-naming-exempt: external contract — desktop ui Menu item field
        trailing: isCurrentOrg ? <Glyph name="check" className="h-4 w-4 text-cobalt" /> : undefined
      };
    })
  }] : [];
  const themeGroup: MenuItem[] = [{
    label: theme === "dark" ? tContextMenu("lightMode") : tContextMenu("darkMode"),
    onSelect: () => setTheme(theme === "dark" ? "light" : "dark")
  }];
  const logoutGroup: MenuItem[] = [{
    label: tNav("logout"),
    onSelect: () => router.push("/logout"),
    isDestructive: true
  }];
  return {
    orgGroup,
    themeGroup,
    logoutGroup
  };
}